import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'gatsby';
import { navLinks } from '@config';
import { KEY_CODES } from '@utils';
import { useOnClickOutside } from '@hooks';

const Menu = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const buttonRef = useRef(null);
  const navRef = useRef(null);

  let menuFocusables;
  let firstFocusableEl;
  let lastFocusableEl;

  const setFocusables = () => {
    menuFocusables = [buttonRef.current, ...Array.from(navRef.current.querySelectorAll('a'))];
    firstFocusableEl = menuFocusables[0];
    lastFocusableEl = menuFocusables[menuFocusables.length - 1];
  };

  const handleBackwardTab = e => {
    if (document.activeElement === firstFocusableEl) {
      e.preventDefault();
      lastFocusableEl.focus();
    }
  };

  const handleForwardTab = e => {
    if (document.activeElement === lastFocusableEl) {
      e.preventDefault();
      firstFocusableEl.focus();
    }
  };

  const onKeyDown = e => {
    switch (e.key) {
      case KEY_CODES.ESCAPE:
      case KEY_CODES.ESCAPE_IE11: {
        setMenuOpen(false);
        break;
      }
      case KEY_CODES.TAB: {
        if (menuFocusables && menuFocusables.length === 1) {
          e.preventDefault();
          break;
        }
        if (e.shiftKey) {
          handleBackwardTab(e);
        } else {
          handleForwardTab(e);
        }
        break;
      }
      default:
        break;
    }
  };

  const onResize = e => {
    if (e.currentTarget.innerWidth > 768) {
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = e => onKeyDown(e);
    const handleResize = e => onResize(e);

    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);

    setFocusables();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen]);

  const wrapperRef = useRef();
  useOnClickOutside(wrapperRef, () => setMenuOpen(false));

  return (
    /* Hidden on desktop, block on mobile */
    <div className="hidden max-[768px]:block">
      <Helmet>
        <body className={menuOpen ? 'blur' : ''} />
      </Helmet>

      <div ref={wrapperRef}>
        {/* Hamburger button — visible only on mobile */}
        <button
          onClick={toggleMenu}
          ref={buttonRef}
          aria-label="Menu"
          className="hidden max-[768px]:flex max-[768px]:justify-center max-[768px]:items-center relative z-[10] -mr-[15px] p-[15px] border-0 bg-transparent text-inherit transition-all [transition-timing-function:linear] [transition-duration:0.15s] [transition-property:opacity,filter]">
          <div
            className="inline-block relative"
            style={{ width: 'var(--hamburger-width)', height: '24px' }}>
            {/* ham-box-inner + pseudo-elements animated via global.css classes */}
            <div className={`ham-box-inner${menuOpen ? ' is-open' : ''}`} />
          </div>
        </button>

        {/* Sliding sidebar */}
        <aside
          aria-hidden={!menuOpen}
          tabIndex={menuOpen ? 0 : -1}
          className="hidden max-[768px]:flex max-[768px]:justify-center max-[768px]:items-center fixed top-0 bottom-0 right-0 p-[50px_10px] w-[min(75vw,400px)] h-screen outline-none bg-light-navy z-[9] transition-all"
          style={{
            transform: `translateX(${menuOpen ? 0 : 100}vw)`,
            visibility: menuOpen ? 'visible' : 'hidden',
            boxShadow: '-10px 0px 30px -15px var(--navy-shadow)',
          }}>

          {/* sidebar-nav counter styles defined in global.css */}
          <nav
            ref={navRef}
            className="sidebar-nav flex justify-between items-center w-full flex-col text-lightest-slate font-mono text-center">

            {navLinks && (
              <ol>
                {navLinks.map(({ url, name }, i) => (
                  <li
                    key={i}
                    className="relative mx-auto mb-[20px] text-[clamp(var(--fz-sm),4vw,var(--fz-lg))] max-[600px]:mb-[10px]">
                    <Link to={url} onClick={() => setMenuOpen(false)}>
                      {name}
                    </Link>
                  </li>
                ))}
              </ol>
            )}

            {/* resume-link class = bigButton styles (global.css) */}
            <a href="/resume.pdf" className="resume-link mt-[10%] mx-auto w-max px-[50px] py-[18px]">
              Resume
            </a>
          </nav>
        </aside>
      </div>
    </div>
  );
};

export default Menu;
